import React, { useRef, useEffect, useState } from 'react';
import Globe from 'react-globe.gl';

const GlobeComponent = () => {
  const globeEl = useRef();
  const [indicesData, setIndicesData] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [hasError, setHasError] = useState(false);

  const validateData = (data) => {
    return data.filter((item) => (
      !isNaN(item.lat) &&
      !isNaN(item.lng) &&
      !isNaN(item.price) &&
      !isNaN(item.change)
    ));
  };

  useEffect(() => {
    if (globeEl.current) {
      const globe = globeEl.current;
      globe.controls().enableZoom = false;
      globe.controls().autoRotate = true;
      globe.controls().autoRotateSpeed = 0.35;

      globe.pointOfView({
        lat: 50.0,
        lng: 10.0,
        altitude: 2.5
      });
    }

    return () => {
      if (globeEl.current) {
        globeEl.current.controls().dispose();
      }
    };
  }, []);

  // Helper function to chunk arrays
  function chunkArray(arr, chunkSize) {
    const chunks = [];
    for (let i = 0; i < arr.length; i += chunkSize) {
      chunks.push(arr.slice(i, i + chunkSize));
    }
    return chunks;
  }




  const fetchIndices = async () => {
    const indices = [
      { symbol: '^FTSE', name: 'FTSE 100', lat: 51.5074, lng: -0.1278 },
      { symbol: '^GDAXI', name: 'DAX', lat: 48.1109, lng: 8.6821 },
      { symbol: 'SPIC25KL.CO', name: 'OMX C25', lat: 55.6761, lng: 12.5683 },
      { symbol: 'IMOEX.ME', name: 'MOEX Russia Index', lat: 55.7558, lng: 37.6176 },
      { symbol: 'OSEAX.OL', name: 'Oslo Stock Exchange', lat: 59.9139, lng: 10.7522 },
      { symbol: 'XU100.IS', name: 'Istanbul 100 Index', lat: 41.0082, lng: 28.9784 },
      { symbol: '^IBEX', name: 'Spain Index', lat: 40.4168, lng: -3.7038 },
      { symbol: '^GSPC', name: 'S&P 500', lat: 40.9128, lng: -74.0060 },
      { symbol: '^IXIC', name: 'Nasdaq', lat: 37.7749, lng: -122.4194 },
      { symbol: '^DJI', name: 'Dow Jones', lat: 35.7128, lng: -78.0060 },
      { symbol: '^BVSP', name: 'Brazil index', lat: -23.5505, lng: -46.6333 },
      { symbol: '^MXX', name: 'Mexico index', lat: 19.4326, lng: -99.1332 },
      { symbol: '^MERV', name: 'Argentina index', lat: -34.6037, lng: -58.3816 },
      { symbol: '^IPSA', name: 'Chile index', lat: -33.4372, lng: -70.6506 },
      { symbol: '^SPCOSLCP', name: 'Colombia index', lat: 4.7110, lng: -74.0721 },
      { symbol: '^SPBLPGPT', name: 'Peru index', lat: -12.0464, lng: -77.0428 },
      { symbol: 'WIUAE.FGI', name: 'UAE index', lat: 25.276987, lng: 55.296249 },
      { symbol: '^TASI.SR', name: 'Tadawul', lat: 24.7136, lng: 46.6753 },
      { symbol: '^N225', name: 'Nikkei 225', lat: 35.6762, lng: 139.6503 },
      { symbol: '^HSI', name: 'Hong Kong index', lat: 22.3964, lng: 114.1095 },
      { symbol: '^BSESN', name: 'India index', lat: 18.9647, lng: 72.8258 },
      { symbol: 'IDX', name: 'Indonesia Index', lat: -6.2088, lng: 106.8456 },
      { symbol: '^KLSE', name: 'Malaysia Index', lat: 3.1390, lng: 101.6869 },
      { symbol: '^TWII', name: 'Taiwan index', lat: 25.0329, lng: 121.5654 },
      { symbol: '^SET.BK', name: 'Thailand index', lat: 13.7563, lng: 100.5018 },
      { symbol: '^VNINDEX.VN', name: 'Vietnam Index', lat: 10.762622, lng: 106.660172 },
      { symbol: '^KS11', name: 'Korea Index', lat: 37.5665, lng: 126.9780 },
      { symbol: '^NZ50', name: 'NZX 50', lat: -41.2865, lng: 174.7762 },
      { symbol: '^AXJO', name: 'ASX 200', lat: -33.8688, lng: 151.2093 },
      { symbol: 'WIZAF.L', name: 'FTSE South Africa', lat: -26.2041, lng: 28.0473 },
      { symbol: '^CASE30', name: 'EGX 30', lat: 30.0451, lng: 31.2457 },
      { symbol: '^NQMA', name: 'Moroccan All', lat: 33.5892, lng: -7.6181 },
      { symbol: '0P0000SSFK.F', name: 'Nigeria all shares', lat: 6.4550, lng: 3.3841 },
      { symbol: 'FNKEN1.L', name: 'Kenya 15 Index', lat: -1.2833, lng: 36.8172 },
      { symbol: 'N098.FGI', name: 'Namibia Overall Index', lat: -22.5609, lng: 17.0658 }
    ];

    try {
      setIsLoading(true);
      let combinedResults = [];
      const chunkedIndices = chunkArray(indices, 5);
  
      for (const chunk of chunkedIndices) {
        const chunkPromises = chunk.map(async (idx) => {
          try {
            console.log(`Fetching data for ${idx.symbol}...`);
            const res = await fetch(
              `https://investorverden.onrender.com/api/stocks?q=${encodeURIComponent(idx.symbol)}`
            );
            if (!res.ok) {
              console.error(`Failed to fetch ${idx.symbol}: ${res.status}`);
              return idx;
            }
            const data = await res.json();
            console.log(`Data received for ${idx.symbol}:`, data);
            
            const result = {
              ...idx,
              price: parseFloat(data?.stockData?.regularMarketPrice) || 0,
              change: parseFloat(data?.stockData?.regularMarketChangePercent) || 0
            };
            console.log(`Processed data for ${idx.symbol}:`, result);
            return result;
          } catch (error) {
            console.error(`Error fetching ${idx.symbol}:`, error);
            return idx;
          }
        });
        const results = await Promise.all(chunkPromises);
        combinedResults = combinedResults.concat(results);
      }
  
      console.log('Final combined results:', combinedResults);
      setIndicesData(combinedResults);
      setIsLoading(false);
    } catch (error) {
      console.error('Error fetching indices:', error);
      setHasError(true);
      setIsLoading(false);
    }
  };

  useEffect(() => {
    fetchIndices();
  }, []);

  useEffect(() => {
    if (globeEl.current && indicesData.length > 0) {
      const globe = globeEl.current;
      globe.controls().enableZoom = false;
      globe.controls().autoRotate = true;
      globe.controls().autoRotateSpeed = 0.35;
      globe.pointOfView(
        {
          lat: 50.0,
          lng: 10.0,
          altitude: 2.5
        },
        0
      );
    }
  }, [indicesData]);

  if (hasError) {
    return <div className="text-red-500">Failed to load globe</div>;
  }






  useEffect(() => {
    document.body.style.overflow = 'hidden';
    return () => {
      document.body.style.overflow = 'unset';
    };
  }, []);

  return (
    <div className="h-screen w-full fixed top-0 left-0 flex justify-center items-center overflow-hidden bg-primary">
      {isLoading && (
        <div className="flex items-center justify-center absolute top-0 left-0 right-0 bottom-0 bg-black bg-opacity-50 z-10">
          <div className="animate-spin rounded-full h-16 w-16 sm:h-32 sm:w-32 border-t-2 border-b-2 border-violet-500" />
        </div>
      )}
      <Globe
        ref={globeEl}
        globeImageUrl="//unpkg.com/three-globe/example/img/earth-dark.jpg"
        backgroundColor="rgba(0,0,0,0)"
        pointsData={indicesData}
        pointLat={d => d.lat}
        pointLng={d => d.lng}
        pointAltitude={0.01}
        pointRadius={0.5}
        pointColor={() => 'white'}
        labelSize={window.innerWidth < 640 ? 0.2 : 0.5}
        labelDotRadius={window.innerWidth < 640 ? 0.15 : 0.3}
        htmlElementsData={indicesData}
        htmlElement={(d) => {
          console.log('Rendering HTML element for:', d); // Debug log
          const el = document.createElement('div');
          el.style.position = 'absolute';
          el.style.transform = 'translate(-50%, -50%)';
          el.style.pointerEvents = 'none';
          el.innerHTML = `
            <div style="
              background: rgba(0,0,0,0.8);
              border: ${d.change > 0 ? '1px solid lightgreen' : '1px solid red'};
              border-radius: 4px;
              padding: ${window.innerWidth < 640 ? '2px 4px' : '4px 8px'};
              font-size: ${window.innerWidth < 640 ? '8px' : '12px'};
              color: ${d.change > 0 ? 'lightgreen' : 'red'};
              white-space: nowrap;
            ">
              ${d.name}: ${Number(d.change).toFixed(2)}%
            </div>
          `;
          return el;
        }}
        htmlAltitude={0.15}
        width={window.innerWidth < 640 ? 450 : 1200}
        height={window.innerWidth < 640 ? 450 : 1000}
      />
    </div>
  );


};

export default GlobeComponent;